import React, { useEffect, useRef, useState } from 'react';
import ThemeSwitcher from '../components/primitives/ThemeSwitcher';
import { Calendar } from '../components';
import '../components/primitives/ThemeSwitcher.css';
import '../theme/semantic.css';
import './ThemePreview.css';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// Utility: collect a list of CSS variable names to display
const GROUPS = [
  {
    title: 'Surfaces',
    vars: ['--semantic-surface-page','--semantic-surface-section','--semantic-surface-panel','--semantic-surface-card']
  },
  {
    title: 'Text',
    vars: ['--color-text-primary','--color-text-secondary','--color-text-muted','--color-text-inverse']
  },
  {
    title: 'Brand & Accent',
    vars: ['--color-brand-primary','--color-brand-primary-hover','--color-brand-accent','--gradient-primary','--gradient-primary-horizontal']
  },
  {
    title: 'Borders',
    vars: ['--color-border-default','--color-border-strong','--border-subtle']
  },
  {
    title: 'Inputs / Tabs',
    vars: ['--semantic-input-bg','--semantic-input-border','--semantic-input-text','--semantic-tabs-bg','--semantic-tabs-bg-active','--semantic-tabs-indicator']
  },
  {
    title: 'Navigation / Sidebar',
    vars: ['--semantic-nav-bg','--semantic-nav-border','--semantic-sidebar-bg','--semantic-sidebar-border','--semantic-sidebar-link-default','--semantic-sidebar-link-hover','--semantic-sidebar-link-active','--semantic-sidebar-link-hover-bg']
  },
  {
    title: 'Statuses / Intents',
    vars: ['--intent-success-bg','--intent-success-text','--intent-warning-bg','--intent-warning-text','--intent-danger-bg','--intent-danger-text','--intent-info-bg','--intent-info-text']
  },
  {
    title: 'Badges',
    vars: ['--badge-positive-bg','--badge-positive-text','--badge-neutral-bg','--badge-neutral-text','--badge-negative-bg','--badge-negative-text']
  },
  {
    title: 'Gradients (Soft)',
    vars: ['--gradient-success-soft','--gradient-info-soft','--gradient-warning-soft','--gradient-neutral-soft']
  },
  {
    title: 'Table / Lists',
    vars: ['--table-header-bg','--table-row-bg','--table-row-alt-bg','--table-row-hover-bg']
  }
  ,{
    title: 'Checkbox',
    vars: [
      '--checkbox-bg',
      '--checkbox-border',
      '--checkbox-bg-hover',
      '--checkbox-border-hover',
      '--checkbox-bg-checked',
      '--checkbox-border-checked',
      '--checkbox-check-color',
      '--checkbox-focus-ring'
    ]
  }
];

function VarSwatch({ name }) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const isGradient = value.startsWith('linear-gradient');
  const style = isGradient ? { backgroundImage: value } : { background: value };
  return (
    <div className="tp-swatch" title={`${name}: ${value}`}>
      <div className="tp-color" style={style} />
      <code className="tp-name">{name}</code>
      <code className="tp-value">{value || '—'}</code>
    </div>
  );
}

export default function ThemePreview() {
  const { currentTheme, persistThemeImmediately } = useTheme();
  useAuth(); // retained in case future user-specific examples are added
  const { addToast } = useToast();
  const [checked, setChecked] = useState(true);

  // Force persistence on each theme change while on the preview page to aid debugging
  const lastPersisted = useRef(null);
  useEffect(() => {
    if (lastPersisted.current === currentTheme) return;
    lastPersisted.current = currentTheme;
    // eslint-disable-next-line no-console
    console.log('[ThemePreview] Theme changed -> forcing immediate persist', currentTheme);
    persistThemeImmediately && persistThemeImmediately();
  }, [currentTheme, persistThemeImmediately]);

  return (
    <div className="theme-preview-page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'var(--space-lg)',flexWrap:'wrap'}}>
        <h1 className="tp-title" style={{margin:0}}>Theme Preview</h1>
        <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
          <button
            type="button"
            className="btn-outline btn-sm"
            onClick={() => addToast({
              intent: 'info',
              message: `Sample toast in "${currentTheme}" theme. Switch themes to compare styling.`,
              duration: 5000,
              action: { label: 'Dismiss', onClick: () => {} }
            })}
          >
            Show Toast
          </button>
          <ThemeSwitcher />
        </div>
      </div>
      <p className="tp-intro">Live extraction of active theme CSS variables. Use this to verify the DocuHounds blue & gold accents plus general semantic coverage. Switch themes to compare.</p>
      <section className="tp-examples" style={{marginTop:'32px'}}>
        <h2>Component Examples</h2>
        <div className="tp-examples-row" style={{flexDirection:'column',alignItems:'stretch',gap:'20px'}}>
          <div style={{display:'flex',flexWrap:'wrap',gap:'16px',alignItems:'center'}}>
            <button className="btn-primary">Primary</button>
            <button className="btn-accent">Accent</button>
            <button className="btn-neutral">Neutral</button>
            <button className="btn-outline">Outline</button>
            <button className="btn-ghost">Ghost</button>
            <button className="btn-danger">Danger</button>
            <button className="btn-subtle">Subtle</button>
            <button className="btn-primary btn-sm">Sm</button>
            <button className="btn-primary btn-lg">Large</button>
            <button className="btn-primary is-loading" aria-disabled="true">Loading</button>
            <button className="btn-primary" disabled>Disabled</button>
            <div style={{position:'relative',display:'inline-block',background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',padding:'40px 60px',borderRadius:'12px'}}>
              <div style={{color:'#fff',fontWeight:600,marginBottom:'8px'}}>Content Behind</div>
              <button className="btn-overlay" style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%, -50%)'}}>Overlay</button>
            </div>
            <input className="tp-input" placeholder="Input field" />
            <label className="tp-checkbox-wrapper" style={{marginLeft:'8px'}}>
              <input
                type="checkbox"
                className="tp-checkbox"
                checked={checked}
                onChange={e=> setChecked(e.target.checked)}
              />
              <span>Example Checkbox</span>
              <span className="tp-checkbox-state">State: {checked ? 'checked' : 'unchecked'}</span>
            </label>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'12px',alignItems:'center'}}>
            <div className="tp-badge success">Success</div>
            <div className="tp-badge danger">Danger</div>
            <div className="tp-badge warning">Warning</div>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'18px'}}>
            <div style={{background:'var(--card-bg)',border:'1px solid var(--border-subtle)',borderRadius:'var(--radius-lg)',padding:'16px 18px',minWidth:'220px',flex:'1 1 220px',boxShadow:'var(--shadow-card)'}}>
              <h3 style={{margin:'0 0 8px',fontSize:'1rem'}}>Card Title</h3>
              <p style={{margin:'0 0 12px',fontSize:'.8rem',color:'var(--text-secondary)'}}>Card body text using secondary color for supportive copy.</p>
              <button className="btn-outline btn-sm">Action</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px',flex:'2 1 340px'}}>
              <div style={{background:'var(--intent-success-bg)',border:'1px solid var(--intent-success-border)',color:'var(--intent-success-text)',padding:'10px 12px',borderRadius:'var(--radius-md)',fontSize:'.75rem',fontWeight:600}}>Success intent message.</div>
              <div style={{background:'var(--intent-warning-bg)',border:'1px solid var(--intent-warning-border)',color:'var(--intent-warning-text)',padding:'10px 12px',borderRadius:'var(--radius-md)',fontSize:'.75rem',fontWeight:600}}>Warning intent message.</div>
              <div style={{background:'var(--intent-danger-bg)',border:'1px solid var(--intent-danger-border)',color:'var(--intent-danger-text)',padding:'10px 12px',borderRadius:'var(--radius-md)',fontSize:'.75rem',fontWeight:600}}>Danger intent message.</div>
              <div style={{background:'var(--intent-info-bg)',border:'1px solid var(--intent-info-border)',color:'var(--intent-info-text)',padding:'10px 12px',borderRadius:'var(--radius-md)',fontSize:'.75rem',fontWeight:600}}>Info intent message.</div>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{borderCollapse:'collapse',minWidth:'560px',fontSize:'.7rem',width:'100%'}}>
              <thead style={{background:'var(--table-header-bg)'}}>
                <tr>
                  {['Name','Status','Updated','Owner','Actions'].map(h=> <th key={h} style={{textAlign:'left',padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)',fontWeight:600}}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {[1,2,3].map(i=> (
                  <tr key={i} style={{background: i%2? 'var(--table-row-bg)':'var(--table-row-alt-bg)'}}>
                    <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)'}}>Item {i}</td>
                    <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)'}}><span className="tp-badge success">OK</span></td>
                    <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)'}}>2025-09-15</td>
                    <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)'}}>User {i}</td>
                    <td style={{padding:'8px 10px',borderBottom:'1px solid var(--border-subtle)'}}><button className="btn-ghost btn-sm">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'24px',alignItems:'flex-start'}}>
            <div style={{flex:'1 1 220px'}}>
              <h1 style={{margin:'0 0 8px'}}>H1 Heading</h1>
              <h2 style={{margin:'0 0 6px'}}>H2 Heading</h2>
              <h3 style={{margin:'0 0 4px'}}>H3 Heading</h3>
              <p style={{margin:'0 0 10px',fontSize:'.85rem'}}>Body text uses <code>--text-primary</code>. <span style={{color:'var(--text-secondary)'}}>Secondary text example</span>. <span style={{color:'var(--text-muted)'}}>Muted text example.</span></p>
              <a href="/" style={{color:'var(--color-brand-primary)'}}>Inline Link</a>
            </div>
            <form style={{display:'flex',flexDirection:'column',gap:'10px',flex:'1 1 220px'}} onSubmit={e=>e.preventDefault()}>
              <label style={{fontSize:'.65rem',fontWeight:600,letterSpacing:'.05em'}}>Email
                <input className="tp-input" type="email" placeholder="name@example.com" style={{marginTop:'4px',width:'100%'}} />
              </label>
              <label style={{fontSize:'.65rem',fontWeight:600,letterSpacing:'.05em'}}>Password
                <input className="tp-input" type="password" placeholder="••••••••" style={{marginTop:'4px',width:'100%'}} />
              </label>
              <div style={{display:'flex',gap:'8px'}}>
                <button className="btn-primary btn-sm" type="submit">Submit</button>
                <button className="btn-outline btn-sm" type="button">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </section>
      <section className="tp-group" style={{marginTop:'32px'}}>
        <h2>Calendar Component</h2>
        <div style={{maxWidth:'100%',overflowX:'auto'}}>
          <Calendar mode="global" />
        </div>
      </section>
      {GROUPS.map(group => (
        <section key={group.title} className="tp-group">
          <h2>{group.title}</h2>
          <div className="tp-grid">
            {group.vars.map(v => <VarSwatch key={v} name={v} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
